"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useLang } from "@/contexts/language-context";
import { Package } from "lucide-react";

export default function MenuBoardsSoldOutScroll() {
  const { t } = useLang();
  const [soldOut, setSoldOut] = useState(false);

  // Cycle: in stock (3s) → sold out (3s) → reset
  useEffect(() => {
    const id = setInterval(() => setSoldOut((v) => !v), 3500);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
                {t("Auto-86", "Auto-86")}
              </div>
              <h2 className="text-4xl md:text-[56px] font-black tracking-[-0.03em] leading-[1.02] mb-6 text-redy-charcoal">
                {t("Out of carnitas? ", "¿Sin carnitas? ")}
                <br />
                <span className="text-redy-charcoal/30">{t("Out on the board.", "Fuera del board.")}</span>
              </h2>
              <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mb-6">
                {t(
                  "When the kitchen runs out, the menu board reflects it within seconds. Item grays out, banner reads SOLD OUT. No more taking an order for something you don't have, no more sad apologies.",
                  "Cuando la cocina se queda sin algo, el menu board lo refleja en segundos. El item se vuelve gris, aparece SOLD OUT. Sin tomar órdenes de lo que no tienes, sin disculpas tristes."
                )}
              </p>

              <div className="inline-flex items-center gap-2.5 bg-white border border-black/5 px-4 py-3 rounded-full shadow-sm">
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(244,114,43,0.12)" }}>
                  <Package className="w-3.5 h-3.5" style={{ color: "#F4722B" }} />
                </div>
                <div className="text-[13px] font-bold text-redy-charcoal">
                  {t("Triggered by inventory hitting zero", "Triggered cuando el inventario llega a cero")}
                </div>
              </div>
            </motion.div>
          </div>

          {/* TV mockup with auto-cycling sold out */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
            >
              <div className="bg-[#0a0a0a] rounded-[10px] p-[8px] shadow-[0_30px_80px_rgba(0,0,0,0.18)]">
                <div className="aspect-[16/10] rounded-[4px] overflow-hidden bg-redy-charcoal relative">
                  {/* Atmosphere */}
                  <div
                    className="absolute inset-0 opacity-30 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(circle at 70% 20%, rgba(244,114,43,0.3) 0%, transparent 60%)",
                    }}
                  />

                  <div className="relative h-full flex flex-col p-6">
                    <div className="text-[12px] font-black uppercase tracking-widest text-white/55 mb-4">
                      Pepitos · {t("Today's tacos", "Tacos de hoy")}
                    </div>

                    <div className="grid grid-cols-2 gap-3 flex-1">
                      {[
                        { name: "Birria", price: "$4.50" },
                        { name: "Al Pastor", price: "$4.00" },
                        {
                          name: "Carnitas",
                          price: "$4.00",
                          isOutOfStock: true,
                        },
                        { name: "Quesabirria", price: "$6.00" },
                      ].map((item) => {
                        const isOut = item.isOutOfStock && soldOut;
                        return (
                          <motion.div
                            key={item.name}
                            animate={{ opacity: isOut ? 0.45 : 1 }}
                            transition={{ duration: 0.5 }}
                            className="bg-white/[0.06] border border-white/10 rounded-md p-3 relative overflow-hidden"
                          >
                            <div
                              className={`text-[14px] font-black text-white ${
                                isOut ? "line-through" : ""
                              }`}
                            >
                              {item.name}
                            </div>
                            <div className="flex items-baseline gap-0.5 text-white">
                              <span className="text-[12px] text-white/55">$</span>
                              <span className={`text-[22px] font-black tabular-nums ${isOut ? "line-through opacity-60" : ""}`}>
                                {item.price.replace("$", "")}
                              </span>
                            </div>

                            <AnimatePresence>
                              {isOut && (
                                <motion.div
                                  initial={{ scale: 1.3, opacity: 0, rotate: -8 }}
                                  animate={{ scale: 1, opacity: 1, rotate: -6 }}
                                  exit={{ scale: 0.8, opacity: 0 }}
                                  transition={{ duration: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
                                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                                >
                                  <div
                                    className="px-4 py-2 rounded text-[14px] font-black uppercase tracking-widest text-white border-2"
                                    style={{
                                      borderColor: "#F4722B",
                                      backgroundColor: "rgba(0,0,0,0.65)",
                                      color: "#F4722B",
                                    }}
                                  >
                                    {t("SOLD OUT", "AGOTADO")}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="h-3 bg-[#0a0a0a] mx-auto" style={{ width: "12%" }} />
              <div className="h-1.5 bg-[#0a0a0a] rounded-b-[4px] mx-auto" style={{ width: "28%" }} />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
